import { useTranslation } from 'react-i18next';

import IconEye from '~icons/bi/eye';
import IconCheck from '~icons/bx/check';

import { EditIcon } from '@adflow/frontend/ui-buttons';
import { HubTooltip, LineClamp, MenuItem } from '@adflow/frontend/ui-common';
import { theme } from '@adflow/frontend/ui-theme';
import type { ViewColumnTemplateDto } from '@adflow/shared/util-column-templates';
import { canUserEditColumnTemplate } from '@adflow/shared/util-column-templates';

import type { ColumnTemplateId, SelectColumnTemplate } from './types';
import { useCanSaveColumnTemplateForAll } from './useCanSaveColumnTemplateForAll';

const COLUMN_TEMPLATE_NAME_LENGTH_TOOLTIP = 64;

interface ColumnTemplateOptionProps {
	template: ViewColumnTemplateDto;
	selected?: boolean;
	selectColumnTemplate: SelectColumnTemplate;
	openColumnTemplateEditor: (templateId: ColumnTemplateId) => void;
}
export function ColumnTemplateOption({
	template,
	selectColumnTemplate,
	openColumnTemplateEditor,
	selected,
}: ColumnTemplateOptionProps) {
	const { t } = useTranslation('common');
	const canSaveAll = useCanSaveColumnTemplateForAll();
	return (
		<HubTooltip
			title={
				template.name.length > COLUMN_TEMPLATE_NAME_LENGTH_TOOLTIP
					? template.name
					: undefined
			}
			placement="left"
		>
			<MenuItem
				css={{
					display: 'grid',
					alignItems: 'center',
					gridTemplateColumns: 'auto 1fr auto',
				}}
				onSelect={() => null}
				onMouseUp={(event) => {
					if (event.button !== 0) return; // Only handle left-click
					selectColumnTemplate(template.id);
				}}
				data-testid="saved-column-template"
			>
				<div
					css={{
						display: 'flex',
						alignItems: 'center',
						minWidth: '24px',
						minHeight: '24px',
					}}
				>
					{selected ? (
						<IconCheck
							css={{ color: theme.colors.text.primary }}
							width={16}
							height={16}
						/>
					) : null}
				</div>
				<LineClamp lines={2}>
					<div css={{ wordBreak: 'break-word' }}>{template.name}</div>
				</LineClamp>
				<HubTooltip
					title={
						!canSaveAll && template.savedAll
							? t('columnTemplate.noSaveAllPermissionTooltip')
							: undefined
					}
				>
					<EditIcon
						onMouseUp={(event) => {
							event.stopPropagation();
							if (event.button !== 0) return; // Only handle left-click
							openColumnTemplateEditor(template.id);
						}}
						// Left margin ensures that template name doesn't come too close to the edit button
						css={{ marginLeft: '0.75rem', borderWidth: 0 }}
						Icon={
							canUserEditColumnTemplate({
								canSaveAll,
								savedAll: template.savedAll,
							})
								? undefined
								: IconEye
						}
						isMenu
					/>
				</HubTooltip>
			</MenuItem>
		</HubTooltip>
	);
}
